import {
  useContext, useEffect, useMemo, useState,
} from 'react';
import BlockItem from '../../../components/BlockItem/BlockItem';
import BlockModal from './BlockModal';
import BlockDeleteModal from './BlockDeleteModal';
import { TemplatesContext } from '../../../contexts/TemplatesContext';
import Template from '../../../models/Template/Template';
import { CategoriesContext } from '../../../contexts/CategoriesContext';
import { PaginationMetaLink } from '../../../models/Paginator';
import Paginator from '../../../components/Paginator';

interface Props {
  type?: string
}

const COUNT_PER_PAGE = 16;

const Index = (props: Props) => {
  const { type } = props;
  const [newBlockModalOpen, setNewBlockModalOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState('');
  const [deletingTemplate, setDeletingTemplate] = useState<Template>(null); // blockId
  const {
    templateList, getAllTemplates, submitTemplate, destroyTemplate,
  } = useContext(TemplatesContext);
  const { requestCategoryList } = useContext(CategoriesContext);

  const [inputText, setInputText] = useState('');
  const [paginationCurrentPage, setPaginationCurrentPage] = useState(1);
  const [paginationLinks, setPaginationLinks] = useState<PaginationMetaLink[]>();
  const [paginationTotal, setPaginationTotal] = useState(0);

  const requestAllTemplates = async (page?: number, perPage?: number) => {
    const result = await getAllTemplates(page, perPage, inputText);
    setPaginationLinks(result.links);
    setPaginationCurrentPage(result.currentPage);
    setPaginationTotal(result.total);
  };

  // fetch all categories;
  useEffect(() => {
    requestCategoryList();
  }, [requestCategoryList]);

  // 最初の場合はすべてのブロックを取得します。
  useEffect(() => {
    requestAllTemplates(1, 16);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const blockType = useMemo(() => {
    if (type === 'system-block') {
      return 'システムブロック';
    }

    return 'マイブロック';
  }, [type]);

  // 新規追加また編集リクエストを投げます
  const handleSubmitTemplate = (template: Template) => {
    setNewBlockModalOpen(false);
    // リクエストが終わりましたら、もう一度fetchします。
    submitTemplate(template, () => {
      requestAllTemplates(paginationCurrentPage, 16);
    });
    setEditingTemplateId('');
  };

  const handleDeleteSubmit = () => {
    if (deletingTemplate === null) {
      return;
    }
    destroyTemplate(deletingTemplate.id, () => {
      setDeletingTemplate(null);
    });
  };

  const handleSubmitSearchText = () => {
    requestAllTemplates(1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmitSearchText();
    }
  };

  const editingTemplate = useMemo(() => templateList.find((t) => t.id === editingTemplateId), [editingTemplateId, templateList]);

  const pageStart = useMemo(() => {
    if (templateList.length === 0) {
      return (paginationCurrentPage - 1) * COUNT_PER_PAGE;
    }

    return (paginationCurrentPage - 1) * COUNT_PER_PAGE + 1;
  }, [templateList, paginationCurrentPage]);

  const pageEnd = useMemo(() => (paginationCurrentPage - 1) * COUNT_PER_PAGE + templateList.length, [templateList, paginationCurrentPage]);

  return (
    (
      <div className="main">
        <main
          style={{
            overflowY: 'auto',
            height: 'calc(100vh - 65px)',
          }}
        >
          <div className="container-fluid p-3">
            <div className="mb-3">
              <h3 className="h3 d-inline align-middle">
                {blockType}
                管理
              </h3>
            </div>
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-header">
                    <h4 className="h4 float-start mt-2">
                      {blockType}
                      一覧
                    </h4>
                    <div className="float-end mr-1">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setNewBlockModalOpen(true)}
                      >
                        新規登録
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row mb-1 block-list-header">
                      <div className="col">
                        <div className="d-flex justify-content-start mb-2">
                          <label className="col-form-label text-nowrap">検索：</label>
                          <input
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            type="text"
                            className="form-control form-search-filter"
                            id="search-filter"
                            name="search-filter"
                          />
                          <button type="button" onClick={handleSubmitSearchText} className="btn btn-primary"><i className="fa-solid fa-magnifying-glass fa-fw" /></button>
                        </div>
                      </div>
                      <div className="col">
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <div>
                            <Paginator
                              links={paginationLinks}
                              changePage={(page) => requestAllTemplates(page, 16)}
                              currentPage={paginationCurrentPage}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      className="block-list blockview"
                      style={{
                        maxHeight: '60vh',
                        overflowY: 'scroll',
                        padding: '0.3rem',
                      }}
                    >
                      {templateList.map((template) => <BlockItem key={template.id} type={template.type} title={template.title} desc={template.description} handleBlockEdit={() => { setEditingTemplateId(template.id); setNewBlockModalOpen(true); }} handleBlockDelete={() => setDeletingTemplate(template)} imgUrl={template.img_url} />)}
                    </div>
                    <div className="row mt-1 block-list-footer">
                      <div className="col-6">
                        <div className="paging-info d-flex justify-content-start align-items-center mt-2">
                          <div>
                            全
                            {paginationTotal}
                            {' '}
                            件中
                            {' '}
                            {pageStart}
                            {' '}
                            〜
                            {pageEnd}
                            {' '}
                            件を表示
                          </div>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="mt-2" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <div>
                            <Paginator
                              links={paginationLinks}
                              changePage={(page) => requestAllTemplates(page, 16)}
                              currentPage={paginationCurrentPage}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <BlockModal
            open={newBlockModalOpen}
            template={editingTemplate}
            handleClose={() => {
              setNewBlockModalOpen(false);
              setEditingTemplateId('');
            }}
            handleSubmit={handleSubmitTemplate}
          />
          <BlockDeleteModal blockTitle={deletingTemplate != null && deletingTemplate.title} open={deletingTemplate != null} handleClose={() => setDeletingTemplate(null)} handleSave={handleDeleteSubmit} />
        </main>
      </div>
    )
  );
};

export default Index;
